import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { catalogAPI } from '../../../services/merchantAPI';

export default function AddToCatalogDialog({ open, onClose, selectedCount, onAdd }) {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedCatalogId, setSelectedCatalogId] = useState('');
  const [newCatalogName, setNewCatalogName] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      loadCatalogs();
      setSelectedCatalogId('');
      setNewCatalogName('');
      setCatalogSearch('');
      setError('');
    }
  }, [open]);

  const loadCatalogs = async () => {
    setLoading(true);
    try {
      const res = await catalogAPI.getCatalogs();
      setCatalogs(res.data.catalogs || []);
    } catch (err) {
      console.error('Failed to load catalogs:', err);
      setError('Failed to load catalogs.');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      let catalogId = selectedCatalogId;

      if (!catalogId) {
        if (!newCatalogName.trim()) {
          setError('Please select an existing catalog or enter a new catalog name.');
          return;
        }

        const res = await catalogAPI.createCatalog({
          name: newCatalogName.trim(),
          description: ''
        });
        catalogId = res.data.catalog.id;
      }

      onAdd(catalogId);
    } catch (err) {
      console.error('Error adding to catalog:', err);
      setError('Failed to process request.');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredCatalogs = catalogs
    .filter(c => c.name.toLowerCase().includes(catalogSearch.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: { xs: '100%', sm: '500px' },
          m: { xs: 0, sm: 'auto' },
          position: 'relative',
          maxWidth: 'calc(100% - 15px)',
          maxHeight: { xs: 'calc(100% - 15px)' },
        }
      }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: '#9CA3AF',
          '&:hover': {
            color: '#4B5563',
          }
        }}
      >
        <Close />
      </IconButton>

      <DialogTitle sx={{ px: 3, pt: 3, pb: 1, pr: 6 }}>
        <Typography sx={{ color: '#111827', fontFamily: 'var(--font-montserrat-semibold, Montserrat)', fontWeight: 600, fontSize: { xs: '19px', sm: '19px', md: '22px' } }}>
          Add {selectedCount} images to Catalog
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 0, pb: 3 }}>
        <Typography sx={{ color: '#6B7280', fontFamily: 'var(--font-montserrat-regular, Montserrat)', fontSize: { xs: '14px', sm: '14px', md: '16px' }, mb: 2 }}>
          Pick an existing catalog or create a new one.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Search field */}
        <Box sx={{ mb: { xs: 2, sm: 4, md: 4 }, pb: 2 }}>
          <TextField
            placeholder="Search catalog name..."
            value={catalogSearch}
            onChange={(e) => setCatalogSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#8224E3' }} />
                </InputAdornment>
              ),
              endAdornment: catalogSearch ? (
                <IconButton
                  size="small"
                  onClick={() => setCatalogSearch('')}
                  edge="end"
                  sx={{ p: '4px', color: '#9CA3AF', '&:hover': { color: '#8224E3', bgcolor: 'transparent' } }}
                >
                  <Close fontSize="small" />
                </IconButton>
              ) : null,
            }}
            sx={{
              fontSize: { xs: '16px', sm: '16px', md: '16px' },
              height: { xs: '1.4375em', sm: '1.4375em', md: '1.4375em' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': { borderColor: '#D1D5DB' },
                '&:hover fieldset': { borderColor: '#9CA3AF' },
                '&.Mui-focused fieldset': { borderColor: '#8224E3' },
              },
              '& .MuiOutlinedInput-input': {
                color: '#111827',
                fontFamily: 'var(--font-montserrat-regular, Montserrat)',
                fontSize: '14px',
                '&::placeholder': { color: '#9CA3AF', opacity: 1 },
              },
            }}
          />
        </Box>

        {/* Catalog list */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress sx={{ color: '#8224E3' }} />
          </Box>
        ) : (
          <Box sx={{
            mb: 3,
            maxHeight: '240px',
            overflowY: 'auto',
            pr: 0.5,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: '#E5E7EB', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb:hover': { background: '#D1D5DB' },
          }}>
            {filteredCatalogs.length === 0 ? (
              <Box p={3} textAlign="center" sx={{ borderBottom: '1px solid #E5E7EB' }}>
                <Typography color="text.secondary" variant="body2">
                  {catalogSearch ? 'No catalogs match your search.' : 'No existing catalogs found.'}
                </Typography>
              </Box>
            ) : (
              filteredCatalogs.map((catalog, idx) => {
                const isSelected = selectedCatalogId === catalog.id;
                return (
                  <Box
                    key={catalog.id}
                    onClick={() => {
                      setSelectedCatalogId(catalog.id);
                      setNewCatalogName('');
                    }}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      px: { xs: 2, sm: 2, md: 2 },
                      py: { xs: 2, sm: 2, md: 2 },
                      cursor: 'pointer',
                      borderBottom: idx < filteredCatalogs.length - 1 ? '1px solid #E5E7EB' : 'none',
                      bgcolor: isSelected ? 'rgba(130, 36, 227, 0.05)' : 'transparent',
                      border: isSelected ? '1px solid #8224E3' : '1px solid transparent',
                      borderRadius: isSelected ? '8px' : '0px',
                      mb: isSelected ? 0.5 : 0,
                      mt: isSelected ? 0.5 : 0,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Typography sx={{
                      fontFamily: 'var(--font-montserrat-semibold, Montserrat)',
                      fontWeight: 600,
                      color: isSelected ? '#8224E3' : '#111827',
                      fontSize: { xs: '14px', sm: '15px', md: '16px' }
                    }}>
                      {catalog.name}
                    </Typography>
                    <Typography sx={{
                      fontFamily: 'var(--font-montserrat-regular, Montserrat)',
                      color: '#9CA3AF',
                      fontSize: { xs: '12px', sm: '14px', md: '14px' }
                    }}>
                      {catalog.item_count || 0} products
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        )}

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', sm: 'center' }
        }}>
          <TextField
            placeholder="New catalog name"
            value={newCatalogName}
            onChange={(e) => {
              setNewCatalogName(e.target.value);
              setSelectedCatalogId('');
            }}
            sx={{
              fontSize: { xs: '16px', sm: '16px', md: '16px' },
              width: { xs: '100%', sm: '270px' },
              '& .MuiOutlinedInput-root': {
                height: '48px',
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: '#D1D5DB',
                },
                '&:hover fieldset': {
                  borderColor: '#9CA3AF',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8224E3',
                },
              },
              '& .MuiOutlinedInput-input': {
                color: '#111827',
                fontSize: '14px',
                fontFamily: 'var(--font-montserrat-regular, Montserrat)',
                '&::placeholder': {
                  color: '#9CA3AF',
                  opacity: 1,
                }
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleAction}
            disabled={submitting || loading || (!selectedCatalogId && !newCatalogName.trim())}
            sx={{
              flex: 1,
              width: { xs: '100%', sm: 'auto' },
              py: { xs: 1.2, sm: 1, md: 2 },
              px: { xs: 2, sm: 2, md: 2 },
              bgcolor: '#8224E3',
              color: 'white',
              textTransform: 'none',
              fontFamily: 'var(--font-montserrat-medium, Montserrat)',
              fontWeight: 500,
              fontSize: '16px',
              height: '48px',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#6C1CBF',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                bgcolor: '#E5E7EB',
                color: '#9CA3AF'
              }
            }}
          >
            {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create OR Add'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
